import {
  CancellationPolicyFieldsDto,
  CancellationPolicyMode,
  DEFAULT_CANCELLATION_POLICY_TEXT,
  DEFAULT_CANCELLATION_POLICY_WINDOW_UNIT,
  DEFAULT_CANCELLATION_POLICY_WINDOW_VALUE,
} from '@shared/core/cancellation-policy'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useCancellationPolicy } from '../../hooks'
import { usePolicyFieldHandlers } from '../../hooks/use-policy-field-handlers'
import type {
  CancellationPolicyFormErrors,
  CancellationPolicyFormValues,
} from '../../types/cancellation-policy-form.types'
import { parsePolicyWindowValue, toAccountPolicyDto, toPolicyFormValues, validatePolicyForm } from '../../utils'
import type { TypeCancellationPolicyFormOptions } from './type-cancellation-policy-panel.interface'

const FALLBACK_FIELDS: CancellationPolicyFieldsDto = {
  notice_value: DEFAULT_CANCELLATION_POLICY_WINDOW_VALUE,
  notice_unit: DEFAULT_CANCELLATION_POLICY_WINDOW_UNIT,
  allow_late_cancel: true,
  allow_reschedule_in_window: true,
  policy_text: DEFAULT_CANCELLATION_POLICY_TEXT,
  policy_text_edited: false,
}

const COMPARED_FIELDS = [
  'notice_value',
  'notice_unit',
  'allow_late_cancel',
  'allow_reschedule_in_window',
  'policy_text',
] as const

function toFormValues(fields: CancellationPolicyFieldsDto): CancellationPolicyFormValues {
  return toPolicyFormValues({ ...fields, enabled: true })
}

function toCustomPolicyDto(values: CancellationPolicyFormValues): CancellationPolicyFieldsDto {
  const dto = toAccountPolicyDto(values)
  return {
    notice_value: dto.notice_value,
    notice_unit: dto.notice_unit,
    allow_late_cancel: dto.allow_late_cancel,
    allow_reschedule_in_window: dto.allow_reschedule_in_window,
    policy_text: dto.policy_text,
    policy_text_edited: dto.policy_text_edited,
  }
}

/** Custom policy form of one appointment type; it lives at modal level so closing the panel keeps it. */
export function useTypeCancellationPolicyForm({ typeId, initialMode }: TypeCancellationPolicyFormOptions) {
  const policyQuery = useCancellationPolicy()
  const account = policyQuery.data
  const stored = typeId ? account?.custom_policies?.[typeId] : undefined
  const form = useForm<CancellationPolicyFormValues>({ defaultValues: toFormValues(FALLBACK_FIELDS) })
  const [errors, setErrors] = useState<CancellationPolicyFormErrors>({})
  const seededRef = useRef(false)
  const values = useWatch({ control: form.control }) as CancellationPolicyFormValues
  const { onNoticeChange, onTextChange } = usePolicyFieldHandlers(form, setErrors)

  const seed = useCallback(() => {
    if (seededRef.current) return
    seededRef.current = true
    form.reset(toFormValues(stored ?? account ?? FALLBACK_FIELDS))
  }, [account, form, stored])

  useEffect(() => {
    if (initialMode === 'custom' && account && !form.formState.isDirty) seed()
  }, [account, form.formState.isDirty, initialMode, seed])

  const validate = useCallback(() => {
    const found = validatePolicyForm(form.getValues())
    setErrors(found)
    return !found.notice_value && !found.policy_text
  }, [form])

  const hasChanges = useCallback(
    (mode: CancellationPolicyMode) => {
      if (mode !== initialMode) return true
      if (mode !== 'custom') return false
      if (!stored) return true
      const current = toCustomPolicyDto(form.getValues())
      return COMPARED_FIELDS.some((field) => current[field] !== stored[field])
    },
    [form, initialMode, stored],
  )

  const customRules = useMemo(() => {
    const noticeValue = parsePolicyWindowValue(values.notice_value, values.notice_unit)
    if (noticeValue === null) return null
    return { ...values, notice_value: noticeValue }
  }, [values])

  return {
    form,
    errors,
    account,
    customRules,
    startCustom: seed,
    validate,
    hasChanges,
    // Unseeded and untouched means the stored policy never loaded: send none, so the server keeps it.
    getCustomPolicy: () =>
      seededRef.current || form.formState.isDirty ? toCustomPolicyDto(form.getValues()) : undefined,
    onNoticeChange,
    onTextChange,
  }
}

export type TypeCancellationPolicyForm = ReturnType<typeof useTypeCancellationPolicyForm>
